import React, { useState } from 'react';

// ─── Policy data ───────────────────────────────────────────────────────────────

const YOUNG_DRIVER_ROWS = [
  {
    vehicleType: 'Private Car (excl. Hire/Reward)',
    licence: 'B',
    age17: '£1,500',
    age1820: '£1,250',
    age2122: '£750',
    age2324: '£500',
  },
  {
    vehicleType: 'Private Car (incl. Hire/Reward)',
    licence: 'B',
    age17: '£1,500',
    age1820: '£1,250',
    age2122: '£1,000',
    age2324: '£750',
  },
  {
    vehicleType: 'Commercial Vehicle <3.5t + trailer ≤750 kg',
    licence: 'B',
    age17: '£2,000',
    age1820: '£1,500',
    age2122: '£1,000',
    age2324: '£750',
  },
  {
    vehicleType: 'Commercial Vehicle 3.5–7.5t + trailer ≤750 kg',
    licence: 'C1',
    age17: null,
    age1820: '£1,500',
    age2122: '£1,500',
    age2324: '£1,000',
  },
  {
    vehicleType: 'Commercial Vehicle 3.5–7.5t + trailer >750 kg (≤12,000 kg GVW)',
    licence: 'C1+E †',
    age17: null,
    age1820: null,
    age2122: '£2,000',
    age2324: '£1,500',
  },
  {
    vehicleType: 'Commercial Vehicle >7.5t + trailer ≤750 kg',
    licence: 'C',
    age17: null,
    age1820: null,
    age2122: '£2,000',
    age2324: '£1,500',
  },
  {
    vehicleType: 'Commercial Vehicle >7.5t + trailer >750 kg (articulated & drawbar)',
    licence: 'C+E',
    age17: null,
    age1820: null,
    age2122: '£2,500',
    age2324: '£2,000',
  },
];

const CONVICTION_ROWS = [
  {
    code: 'AC',
    description: 'Failing to Stop',
    excess: '£500',
  },
  {
    code: 'BA',
    description: 'Disqualified Driving',
    excess: '£1,000',
  },
  {
    code: 'CD',
    description: 'Careless Driving — incl. CD33, CD40, CD50, CD60, CD70, CD80, CD90',
    excess: '£1,000',
  },
  {
    code: 'DR / DG',
    description: 'Drink or Drug Driving related offences',
    excess: '£1,000',
  },
  {
    code: 'TT99',
    description: 'Driving Bans of up to 6 months',
    excess: '£1,000',
  },
  {
    code: '—',
    description: 'Any offence or combination of offences resulting in a driving suspension, or an accumulation of 12 or more penalty points within a 3-year period',
    excess: '£1,000',
  },
];

const RESTRICTIONS = [
  'Drivers aged 17 are restricted to Private Cars and Commercial Vehicles under 3.5t only.',
  'Drivers aged 18–20 are NOT permitted to drive Commercial Vehicles exceeding 7.5t.',
  'All drivers must hold the correct licence category for their vehicle type.',
  'Additional excesses are applied per incident and are non-negotiable.',
  'Where both young driver and conviction excesses apply, both are charged.',
  'All relevant convictions must be declared to Myshield before being added to the policy. Non-disclosure may result in a claim being declined.',
  'Any new penalty points or driving offences must be reported to the fleet manager within 14 days of occurrence.',
];

// ─── Styles ────────────────────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  backgroundColor: '#78350f',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  padding: '8px 10px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  borderRight: '1px solid #92400e',
};

const TH_LEFT: React.CSSProperties = { ...TH, textAlign: 'left', borderRight: '1px solid #92400e' };

const TD_AMT = (val: string | null, rowEven: boolean): React.CSSProperties => ({
  padding: '7px 10px',
  textAlign: 'center',
  fontSize: 13,
  fontWeight: val ? 600 : 400,
  color: val ? '#166534' : '#9ca3af',
  fontStyle: val ? 'normal' : 'italic',
  backgroundColor: rowEven ? '#fffff5' : '#fffbeb',
  borderRight: '1px solid #fde68a',
  whiteSpace: 'nowrap',
});

const TD_LABEL = (rowEven: boolean): React.CSSProperties => ({
  padding: '7px 10px',
  fontSize: 13,
  color: '#78350f',
  backgroundColor: rowEven ? '#fffff5' : '#fffbeb',
  borderRight: '1px solid #fde68a',
});

const TD_LIC = (rowEven: boolean): React.CSSProperties => ({
  padding: '7px 10px',
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 700,
  color: '#1e40af',
  backgroundColor: rowEven ? '#fffff5' : '#fffbeb',
  borderRight: '1px solid #fde68a',
  whiteSpace: 'nowrap',
});

const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  border: '1px solid #fde68a',
  borderRadius: 6,
  overflow: 'hidden',
  fontSize: 13,
  marginBottom: 6,
};

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  /** Optional extra client-specific policy notes from the database */
  additionalNotes?: string | null;
  /** Whether the viewer is an admin (shows extra edit context) */
  isAdmin?: boolean;
}

const NamedDriverPolicyBanner: React.FC<Props> = ({ additionalNotes, isAdmin }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{
      marginBottom: 24,
      backgroundColor: '#fffbeb',
      border: '1px solid #f59e0b',
      borderRadius: 10,
      borderLeft: '4px solid #f59e0b',
      overflow: 'hidden',
    }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#92400e' }}>
          📋 Named Driver &amp; Approved Driver Criteria — Motor Fleet Policy
        </h3>
        <span style={{ fontSize: 13, color: '#b45309', fontWeight: 600 }}>
          {expanded ? 'Hide ▲' : 'Show ▼'}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 18px' }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>
            The following additional excesses and restrictions apply to all named drivers on this motor fleet policy.
            All fleet operators must ensure named drivers are aware of these terms before being added to the policy.
          </p>

          {/* ── Section 1: Young Driver Table ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{
              backgroundColor: '#92400e',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 10px',
              borderRadius: '6px 6px 0 0',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}>
              Section 1 — Young Driver Additional Excess
            </div>
            <p style={{ margin: '8px 0 8px', fontSize: 12, color: '#92400e', fontStyle: 'italic' }}>
              Additional excess per incident applies to drivers under 25 years of age.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={TABLE_STYLE}>
                <thead>
                  <tr>
                    <th style={{ ...TH_LEFT, minWidth: 260 }}>Vehicle Type</th>
                    <th style={{ ...TH, width: 70 }}>Req. Licence</th>
                    <th style={{ ...TH, width: 72 }}>Age 17</th>
                    <th style={{ ...TH, width: 80 }}>Age 18–20</th>
                    <th style={{ ...TH, width: 80 }}>Age 21–22</th>
                    <th style={{ ...TH, width: 80, borderRight: 'none' }}>Age 23–24</th>
                  </tr>
                </thead>
                <tbody>
                  {YOUNG_DRIVER_ROWS.map((row, i) => {
                    const even = i % 2 === 0;
                    return (
                      <tr key={i}>
                        <td style={TD_LABEL(even)}>{row.vehicleType}</td>
                        <td style={TD_LIC(even)}>{row.licence}</td>
                        <td style={TD_AMT(row.age17, even)}>{row.age17 ?? '—'}</td>
                        <td style={TD_AMT(row.age1820, even)}>{row.age1820 ?? '—'}</td>
                        <td style={TD_AMT(row.age2122, even)}>{row.age2122 ?? '—'}</td>
                        <td style={{ ...TD_AMT(row.age2324, even), borderRight: 'none' }}>{row.age2324 ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#92400e' }}>
              † C1+E: or Category B if driving test passed before 01/01/1997 (GVW not exceeding 8,250 kg) &nbsp;|&nbsp;
              <strong>—</strong> = this vehicle / age combination is <strong>not permitted</strong> under this policy
            </p>
          </div>

          {/* ── Section 2: Conviction Excess Table ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{
              backgroundColor: '#92400e',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 10px',
              borderRadius: '6px 6px 0 0',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}>
              Section 2 — Driving Conviction Additional Excess
            </div>
            <p style={{ margin: '8px 0 8px', fontSize: 12, color: '#92400e', fontStyle: 'italic' }}>
              These excesses apply regardless of driver age and are in addition to any other excess.
              All relevant convictions must be declared before the driver is added to the policy.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={TABLE_STYLE}>
                <thead>
                  <tr>
                    <th style={{ ...TH, width: 90 }}>Code</th>
                    <th style={TH_LEFT}>Offence Type</th>
                    <th style={{ ...TH, width: 130, borderRight: 'none' }}>Additional Excess</th>
                  </tr>
                </thead>
                <tbody>
                  {CONVICTION_ROWS.map((row, i) => {
                    const even = i % 2 === 0;
                    return (
                      <tr key={i}>
                        <td style={{ ...TD_LIC(even), textAlign: 'center' }}>{row.code}</td>
                        <td style={TD_LABEL(even)}>{row.description}</td>
                        <td style={{ ...TD_AMT(row.excess, even), borderRight: 'none' }}>{row.excess}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── General Restrictions ── */}
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: additionalNotes ? 16 : 0,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              General Motor Fleet Restrictions
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc' }}>
              {RESTRICTIONS.map((r, i) => (
                <li key={i} style={{ fontSize: 12, color: '#78350f', lineHeight: 1.65, marginBottom: 2 }}>{r}</li>
              ))}
            </ul>
          </div>

          {/* ── Additional client-specific notes (from DB) ── */}
          {additionalNotes && (
            <div style={{ marginTop: 14, padding: '10px 14px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9a3412', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Additional Client Criteria
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#7c2d12', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {additionalNotes}
              </p>
            </div>
          )}

          {isAdmin && !additionalNotes && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#a16207', fontStyle: 'italic' }}>
              No additional client-specific criteria set. Edit the Named Driver Policy in the Client Record to add custom notes.
            </p>
          )}

          <p style={{ margin: '12px 0 0', fontSize: 11, color: '#a16207', textAlign: 'right' }}>
            This policy is reviewed annually. Last reviewed: March 2026.
          </p>
        </div>
      )}
    </div>
  );
};

export default NamedDriverPolicyBanner;
